import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import {
  ItemGroup,
  InventoryItem,
  StockMovement,
  InventoryOverviewStats,
  ItemsQueryParams,
  CreateItemGroupDto,
  UpdateItemGroupDto,
  CreateInventoryItemDto,
  UpdateInventoryItemDto,
  StockInDto,
  StockOutDto,
  StockAdjustmentDto,
  CreateItemVariantDto,
  UpdateItemVariantDto,
  StockOutPreview,
  StockBatch,
  Vendor,
  CreateVendorDto,
  UpdateVendorDto,
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchaseOrdersQueryParams,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  ReceiveDeliveryDto,
  PickList,
  PickListsQueryParams,
  CreatePickListDto,
  FulfillPickListDto,
} from './inventoryTypes';
import { accountingApi } from 'features/accounting/accountingApi';
import * as Sentry from '@sentry/react';

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['InventoryOverview', 'ItemGroups', 'ItemGroup', 'InventoryItems', 'InventoryItem', 'StockMovements', 'Vendors', 'Vendor', 'PurchaseOrders', 'PurchaseOrder', 'PickLists', 'PickList'],
  endpoints: (builder) => ({
    // ==================
    // Overview & Stats
    // ==================

    getInventoryOverview: builder.query<InventoryOverviewStats, void>({
      query: () => ({
        url: endpoints.inventory.overview,
        method: 'GET',
      }),
      providesTags: ['InventoryOverview'],
    }),

    getLowStockItems: builder.query<InventoryItem[], void>({
      query: () => ({
        url: endpoints.inventory.lowStock,
        method: 'GET',
      }),
      providesTags: ['InventoryItems'],
    }),

    getRecentMovements: builder.query<StockMovement[], number | void>({
      query: (limit = 10) => ({
        url: endpoints.inventory.recentMovements,
        method: 'GET',
        params: { limit },
      }),
      providesTags: ['StockMovements'],
    }),

    // ==================
    // Item Groups
    // ==================

    getItemGroups: builder.query<ItemGroup[], void>({
      query: () => ({
        url: endpoints.inventory.itemGroups,
        method: 'GET',
      }),
      providesTags: ['ItemGroups'],
    }),

    getItemGroup: builder.query<ItemGroup, string>({
      query: (id) => ({
        url: `${endpoints.inventory.itemGroups}/${id}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'ItemGroup', id }],
    }),

    createItemGroup: builder.mutation<ItemGroup, CreateItemGroupDto>({
      query: (data) => ({
        url: endpoints.inventory.itemGroups,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ItemGroups'],
    }),

    updateItemGroup: builder.mutation<ItemGroup, { id: string; data: UpdateItemGroupDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.itemGroups}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'ItemGroup', id },
        'ItemGroups',
      ],
    }),

    deleteItemGroup: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `${endpoints.inventory.itemGroups}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ItemGroups'],
    }),

    getItemsInGroup: builder.query<InventoryItem[], string>({
      query: (groupId) => ({
        url: `${endpoints.inventory.itemGroups}/${groupId}/items`,
        method: 'GET',
      }),
      providesTags: ['InventoryItems'],
    }),

    unassignItemsFromGroup: builder.mutation<
      { message: string; unassignedCount: number },
      string
    >({
      query: (groupId) => ({
        url: `${endpoints.inventory.itemGroups}/${groupId}/unassign-items`,
        method: 'POST',
      }),
      invalidatesTags: ['InventoryItems', 'ItemGroups'],
    }),

    // ==================
    // Inventory Items
    // ==================

    getInventoryItems: builder.query<InventoryItem[], ItemsQueryParams | void>({
      query: (params) => ({
        url: endpoints.inventory.items,
        method: 'GET',
        params: params ? {
          search: params.search,
          groupId: params.groupId,
          lowStockOnly: params.lowStockOnly ? 'true' : undefined,
          includeInactive: params.includeInactive ? 'true' : undefined,
        } : undefined,
      }),
      providesTags: ['InventoryItems'],
    }),

    getInventoryItem: builder.query<InventoryItem, string>({
      query: (id) => ({
        url: `${endpoints.inventory.items}/${id}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'InventoryItem', id }],
    }),

    createInventoryItem: builder.mutation<InventoryItem, CreateInventoryItemDto>({
      query: (data) => ({
        url: endpoints.inventory.items,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['InventoryItems', 'InventoryOverview', 'ItemGroups'],
    }),

    updateInventoryItem: builder.mutation<InventoryItem, { id: string; data: UpdateInventoryItemDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.items}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'InventoryItem', id },
        'InventoryItems',
        'InventoryOverview',
      ],
    }),

    deleteInventoryItem: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `${endpoints.inventory.items}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InventoryItems', 'InventoryOverview', 'ItemGroups'],
    }),

    // ==================
    // Stock Operations
    // ==================

    stockIn: builder.mutation<InventoryItem, { id: string; data: StockInDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.items}/${id}/stock-in`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'InventoryItem', id },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted({ id, data }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'stock_in', api: 'inventoryApi' },
            extra: { itemId: id, quantity: data.quantity },
          });
        }
      },
    }),

    stockOut: builder.mutation<InventoryItem, { id: string; data: StockOutDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.items}/${id}/stock-out`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'InventoryItem', id },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted({ id, data }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'stock_out', api: 'inventoryApi' },
            extra: { itemId: id, quantity: data.quantity, projectId: data.projectId },
          });
        }
      },
    }),

    adjustStock: builder.mutation<InventoryItem, { id: string; data: StockAdjustmentDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.items}/${id}/adjust`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'InventoryItem', id },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted({ id, data }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'stock_adjust', api: 'inventoryApi' },
            extra: { itemId: id, newQuantity: data.newQuantity, reason: data.reason },
          });
        }
      },
    }),

    getItemMovements: builder.query<StockMovement[], { id: string; limit?: number }>({
      query: ({ id, limit = 50 }) => ({
        url: `${endpoints.inventory.items}/${id}/movements`,
        method: 'GET',
        params: { limit },
      }),
      providesTags: ['StockMovements'],
    }),

    // FIFO Preview & Batches
    previewStockOut: builder.mutation<StockOutPreview, { id: string; quantity: number }>({
      query: ({ id, quantity }) => ({
        url: `${endpoints.inventory.items}/${id}/stock-out/preview`,
        method: 'POST',
        data: { quantity },
      }),
    }),

    getItemBatches: builder.query<StockBatch[], string>({
      query: (id) => ({
        url: `${endpoints.inventory.items}/${id}/batches`,
        method: 'GET',
      }),
    }),

    getVariantBatches: builder.query<StockBatch[], { itemId: string; variantId: string }>({
      query: ({ itemId, variantId }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}/batches`,
        method: 'GET',
      }),
    }),

    // ==================
    // Item Variants
    // ==================

    createVariant: builder.mutation<InventoryItem, { itemId: string; data: CreateItemVariantDto }>({
      query: ({ itemId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
      ],
    }),

    updateVariant: builder.mutation<InventoryItem, { itemId: string; variantId: string; data: UpdateItemVariantDto }>({
      query: ({ itemId, variantId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
      ],
    }),

    deleteVariant: builder.mutation<InventoryItem, { itemId: string; variantId: string }>({
      query: ({ itemId, variantId }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
      ],
    }),

    // Variant Stock Operations
    stockInVariant: builder.mutation<StockMovement, { itemId: string; variantId: string; data: StockInDto }>({
      query: ({ itemId, variantId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}/stock-in`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
    }),

    stockOutVariant: builder.mutation<StockMovement, { itemId: string; variantId: string; data: StockOutDto }>({
      query: ({ itemId, variantId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}/stock-out`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
    }),

    adjustVariantStock: builder.mutation<StockMovement, { itemId: string; variantId: string; data: StockAdjustmentDto }>({
      query: ({ itemId, variantId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}/adjust`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
    }),

    // Variant FIFO Preview
    previewStockOutVariant: builder.mutation<StockOutPreview, { itemId: string; variantId: string; quantity: number }>({
      query: ({ itemId, variantId, quantity }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/${variantId}/stock-out/preview`,
        method: 'POST',
        data: { quantity },
      }),
    }),

    // Bulk Create Variants
    bulkCreateVariants: builder.mutation<
      { created: number; skipped: number; errors: string[] },
      {
        itemId: string;
        data: {
          variants: Array<{ attributes: Record<string, string>; quantity?: number; unitCost?: number }>;
          defaultUnitCost?: number;
          defaultMinStock?: number;
        };
      }
    >({
      query: ({ itemId, data }) => ({
        url: `${endpoints.inventory.items}/${itemId}/variants/bulk`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { itemId }) => [
        { type: 'InventoryItem', id: itemId },
        'InventoryItems',
        'InventoryOverview',
      ],
    }),

    // ==================
    // AI Suggestions
    // ==================

    suggestVariantAttributes: builder.mutation<
      { attributes: Array<{ name: string; values: string[]; required: boolean }>; reasoning: string },
      { itemName: string; itemDescription?: string; existingAttributes?: string[] }
    >({
      query: (data) => ({
        url: '/api/ai/suggest-variant-attributes',
        method: 'POST',
        data,
      }),
    }),

    getAiStatus: builder.query<{ available: boolean }, void>({
      query: () => ({
        url: '/api/ai/status',
        method: 'GET',
      }),
    }),

    // ==================
    // Vendors
    // ==================

    getVendors: builder.query<Vendor[], void>({
      query: () => ({
        url: endpoints.inventory.vendors,
        method: 'GET',
      }),
      providesTags: ['Vendors'],
    }),

    getVendor: builder.query<Vendor, string>({
      query: (id) => ({
        url: `${endpoints.inventory.vendors}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Vendor', id }],
    }),

    createVendor: builder.mutation<Vendor, CreateVendorDto>({
      query: (data) => ({
        url: endpoints.inventory.vendors,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Vendors'],
    }),

    updateVendor: builder.mutation<Vendor, { id: string; data: UpdateVendorDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.vendors}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Vendor', id },
        'Vendors',
      ],
    }),

    deleteVendor: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `${endpoints.inventory.vendors}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vendors'],
    }),

    getVendorItems: builder.query<InventoryItem[], string>({
      query: (vendorId) => ({
        url: `${endpoints.inventory.vendors}/${vendorId}/items`,
        method: 'GET',
      }),
      providesTags: ['InventoryItems'],
    }),

    // ==================
    // Purchase Orders
    // ==================

    getPurchaseOrders: builder.query<PurchaseOrder[], PurchaseOrdersQueryParams | void>({
      query: (params) => ({
        url: endpoints.inventory.purchaseOrders,
        method: 'GET',
        params: params ? {
          status: params.status,
          vendorId: params.vendorId,
        } : undefined,
      }),
      providesTags: ['PurchaseOrders'],
    }),

    getPurchaseOrder: builder.query<PurchaseOrder, string>({
      query: (id) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'PurchaseOrder', id }],
    }),

    createPurchaseOrder: builder.mutation<PurchaseOrder, CreatePurchaseOrderDto>({
      query: (data) => ({
        url: endpoints.inventory.purchaseOrders,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PurchaseOrders'],
    }),

    updatePurchaseOrder: builder.mutation<PurchaseOrder, { id: string; data: UpdatePurchaseOrderDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PurchaseOrder', id },
        'PurchaseOrders',
      ],
    }),

    deletePurchaseOrder: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PurchaseOrders'],
    }),

    receivePurchaseOrder: builder.mutation<PurchaseOrder, string>({
      query: (id) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}/receive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'PurchaseOrder', id },
        'PurchaseOrders',
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate accounting API cache to refresh material costs
          dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'po_receive', api: 'inventoryApi' },
            extra: { purchaseOrderId: id },
          });
        }
      },
    }),

    receiveDelivery: builder.mutation<PurchaseOrder, { id: string; data: ReceiveDeliveryDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}/receive-delivery`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PurchaseOrder', id },
        'PurchaseOrders',
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate accounting API cache to refresh material costs
          dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'po_receive_delivery', api: 'inventoryApi' },
            extra: { purchaseOrderId: id, itemCount: data.items?.length || 0 },
          });
        }
      },
    }),

    getOpenPurchaseOrdersByVendor: builder.query<PurchaseOrder[], string>({
      query: (vendorId) => ({
        url: endpoints.inventory.purchaseOrders,
        method: 'GET',
        params: { vendorId },
      }),
      transformResponse: (response: PurchaseOrder[]) =>
        response.filter(
          (po) =>
            po.status === PurchaseOrderStatus.CONFIRMED ||
            po.status === PurchaseOrderStatus.PARTIAL_RECEIVED
        ),
      providesTags: ['PurchaseOrders'],
    }),

    cancelPurchaseOrder: builder.mutation<PurchaseOrder, string>({
      query: (id) => ({
        url: `${endpoints.inventory.purchaseOrders}/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'PurchaseOrder', id },
        'PurchaseOrders',
      ],
      async onQueryStarted(id, { queryFulfilled }) {
        Sentry.addBreadcrumb({
          category: 'inventory',
          message: `Cancelling purchase order ${id}`,
          level: 'warning',
        });
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'po_cancel', api: 'inventoryApi' },
            extra: { purchaseOrderId: id },
          });
        }
      },
    }),

    // ==================
    // Pick Lists
    // ==================

    getPickLists: builder.query<PickList[], PickListsQueryParams | void>({
      query: (params) => ({
        url: endpoints.inventory.pickLists,
        method: 'GET',
        params: params ? {
          status: params.status,
          jobType: params.jobType,
          projectId: params.projectId,
          workOrderId: params.workOrderId,
        } : undefined,
      }),
      providesTags: ['PickLists'],
    }),

    getPickList: builder.query<PickList, string>({
      query: (id) => ({
        url: `${endpoints.inventory.pickLists}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'PickList', id }],
    }),

    createPickList: builder.mutation<PickList, CreatePickListDto>({
      query: (data) => ({
        url: endpoints.inventory.pickLists,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PickLists', 'InventoryItems', 'InventoryOverview'],
    }),

    fulfillPickList: builder.mutation<PickList, { id: string; data: FulfillPickListDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.inventory.pickLists}/${id}/fulfill`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PickList', id },
        'PickLists',
        'InventoryItems',
        'InventoryOverview',
        'StockMovements',
      ],
      async onQueryStarted({ id, data }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'picklist_fulfill', api: 'inventoryApi' },
            extra: { pickListId: id, itemCount: data.items?.length || 0 },
          });
        }
      },
    }),

    cancelPickList: builder.mutation<PickList, string>({
      query: (id) => ({
        url: `${endpoints.inventory.pickLists}/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'PickList', id },
        'PickLists',
        'InventoryItems',
        'InventoryOverview',
      ],
      async onQueryStarted(id, { queryFulfilled }) {
        Sentry.addBreadcrumb({
          category: 'inventory',
          message: `Cancelling pick list ${id}`,
          level: 'warning',
        });
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'picklist_cancel', api: 'inventoryApi' },
            extra: { pickListId: id },
          });
        }
      },
    }),

    getItemAvailableQuantity: builder.query<{ availableQuantity: number }, { itemId: string; variantId?: string }>({
      query: ({ itemId, variantId }) => ({
        url: `${endpoints.inventory.items}/${itemId}/available-quantity`,
        method: 'GET',
        params: variantId ? { variantId } : undefined,
      }),
      providesTags: ['InventoryItems'],
    }),
  }),
});

export const {
  // Overview & Stats
  useGetInventoryOverviewQuery,
  useGetLowStockItemsQuery,
  useGetRecentMovementsQuery,

  // Item Groups
  useGetItemGroupsQuery,
  useGetItemGroupQuery,
  useCreateItemGroupMutation,
  useUpdateItemGroupMutation,
  useDeleteItemGroupMutation,
  useGetItemsInGroupQuery,
  useLazyGetItemsInGroupQuery,
  useUnassignItemsFromGroupMutation,

  // Inventory Items
  useGetInventoryItemsQuery,
  useGetInventoryItemQuery,
  useCreateInventoryItemMutation,
  useUpdateInventoryItemMutation,
  useDeleteInventoryItemMutation,

  // Stock Operations
  useStockInMutation,
  useStockOutMutation,
  useAdjustStockMutation,
  useGetItemMovementsQuery,

  // FIFO Preview & Batches
  usePreviewStockOutMutation,
  useGetItemBatchesQuery,
  useLazyGetItemBatchesQuery,
  useGetVariantBatchesQuery,
  useLazyGetVariantBatchesQuery,

  // Item Variants
  useCreateVariantMutation,
  useUpdateVariantMutation,
  useDeleteVariantMutation,
  useStockInVariantMutation,
  useStockOutVariantMutation,
  useAdjustVariantStockMutation,
  usePreviewStockOutVariantMutation,
  useBulkCreateVariantsMutation,

  // AI Suggestions
  useSuggestVariantAttributesMutation,
  useGetAiStatusQuery,

  // Vendors
  useGetVendorsQuery,
  useGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useGetVendorItemsQuery,
  useLazyGetVendorItemsQuery,

  // Purchase Orders
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useReceivePurchaseOrderMutation,
  useReceiveDeliveryMutation,
  useGetOpenPurchaseOrdersByVendorQuery,
  useLazyGetOpenPurchaseOrdersByVendorQuery,
  useCancelPurchaseOrderMutation,

  // Pick Lists
  useGetPickListsQuery,
  useGetPickListQuery,
  useLazyGetPickListQuery,
  useCreatePickListMutation,
  useFulfillPickListMutation,
  useCancelPickListMutation,
  useGetItemAvailableQuantityQuery,
  useLazyGetItemAvailableQuantityQuery,
} = inventoryApi;
